/**
 * Custom hook for handling image file previews
 * Manages file reading, loading states, error handling, and aspect ratio calculation
 */

import { useState, useEffect } from 'react';
import { WhatsAppMediaType } from '../../../../utils/whatsapp/media.util';

interface UseImagePreviewReturn {
  /** Base64 preview URL for the image */
  preview: string | null;
  /** Whether there was an error loading the preview */
  previewError: boolean;
  /** Whether the image has finished loading */
  imageLoaded: boolean;
  /** Aspect ratio of the loaded image (width/height) */
  imageAspectRatio: number | null;
  /** Handler for when image loads successfully */
  handleImageLoad: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  /** Handler for when image fails to load */
  handleImageError: () => void;
}

/**
 * Hook for managing image file previews
 * @param file - The image file to preview
 * @param mediaType - The media type of the image
 * @returns An object containing the preview, preview error, image loaded, image aspect ratio, handle image load, and handle image error
 */
export const useImagePreview = (
  file: File,
  mediaType: WhatsAppMediaType
): UseImagePreviewReturn => {
  const [preview, setPreview] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);

  const cleanup = () => {
    setPreview(null);
    setPreviewError(false);
    setImageLoaded(false);
    setImageAspectRatio(null);
  };

  useEffect(() => {
    cleanup();

    if (mediaType === WhatsAppMediaType.IMAGE && file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
        setPreviewError(false);
      };
      reader.onerror = () => {
        setPreviewError(true);
        setImageLoaded(false);
      };
      reader.readAsDataURL(file);
    }

    return () => {
      cleanup();
    };
  }, [file, mediaType]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatio(aspectRatio);
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setPreviewError(true);
    setImageLoaded(false);
  };

  return {
    preview,
    previewError,
    imageLoaded,
    imageAspectRatio,
    handleImageLoad,
    handleImageError,
  };
};
